import { useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import { useCallback, useEffect, useMemo, useRef } from 'react';

import useStatementDocumentWrites, {
    ERROR_SNACKBAR_DURATION_MS,
    SAVE_FAILED_MESSAGE,
} from 'modules/common/components/VerifiedStatementUpload/hooks/useStatementDocumentWrites';
import { accountKey } from 'modules/common/components/VerifiedStatementUpload/utils/periods';
import { Document } from 'modules/common/interfaces/documents';
import {
    AccountStatementCoverage,
    STATEMENT_COVERAGE_QUERY_KEY,
    StatementPeriod,
} from 'services/bookkeeping/queries/useStatementCoverage';
import {
    OnboardingProgress,
    useAccountingOnboardingProgress,
} from 'services/bookkeeping/queries/useAccountingOnboardingProgress';
import { StatementPeriodAssignment } from 'services/bookkeeping/mutations/useReassignStatementPeriods';
import useUpdateOnboardingStep from 'services/bookkeeping/mutations/useUpdateOnboardingStep';
import { STATEMENT_UPLOAD_STATUSES_QUERY_KEY } from 'services/bookkeeping/queries/useStatementUploadStatuses';
import { sendDatadogRumError } from 'utils/analytics/datadogRum';

type UploadedStatement = NonNullable<OnboardingProgress['statement_upload_data']>['uploaded_statements'][number];

const ONBOARDING_STEP = 'statement_upload';

/**
 * The progress query this hook both reads and writes. Local rather than shared: the key is spelled
 * inline at every other call site in the codebase.
 */
const PROGRESS_QUERY_KEY = ['accounting-onboarding-progress'];

/**
 * Re-exported, not redefined. The writes that raise these moved to `useStatementDocumentWrites`
 * when a second surface needed them, and this hook's callers still import them from here.
 */
export {
    DELETE_FAILED_MESSAGE,
    PERIODS_SAVED_MESSAGE,
    SAVE_FAILED_MESSAGE,
} from 'modules/common/components/VerifiedStatementUpload/hooks/useStatementDocumentWrites';

/**
 * Shown when the write recording this step's uploads fails.
 *
 * Defined here rather than beside the shared writes because the step record is this hook's alone —
 * no other surface has one. It lands *after* the dropzone has already said "Document uploaded
 * successfully!", so without it the member reads a success message above an empty list.
 */
export const UPLOAD_SAVE_FAILED_MESSAGE =
    "Your file was uploaded, but we couldn't add it to this account. Please try again.";

interface StatementStepWrites {
    /** The uploads this step has recorded, straight from the step blob. */
    uploadedStatements: UploadedStatement[];
    /** Accounts the member has said they can't supply, keyed by `accountKey`. */
    acknowledgedAccounts: Set<string>;
    /** Record a finished upload batch against the card it was dropped on. */
    saveUploads: (uploaded: Document[], account: AccountStatementCoverage) => Promise<void>;
    /** Take a file off one card (one month, or all of it), dropping its entry once it holds none there. */
    removeUpload: (documentUuid: string, account: AccountStatementCoverage, period?: StatementPeriod) => Promise<void>;
    /** Send a session's month corrections as one request. Resolves whether it landed. */
    savePeriods: (
        assignments: StatementPeriodAssignment[],
        bankAccountId: string | null | undefined
    ) => Promise<boolean>;
    /**
     * Persist the waiver set. Resolves whether it landed.
     *
     * The updater runs inside the queued write, on the freshest set we know of. A set built out in
     * a render is stale the moment a second waiver queues behind it: the blob is replaced
     * wholesale, so two accounts waived before the progress refetch lands would each send a set
     * the other is missing from, and the later write would silently un-waive the earlier one.
     */
    saveAcknowledged: (update: (current: Set<string>) => Set<string>) => Promise<boolean>;
    /**
     * Refresh coverage if — and only if — this session uploaded something.
     *
     * Called when the modal closes. Coverage is a Tight plus Plaid round trip, and a member
     * who opened a card just to read the month list should not pay for one.
     */
    refreshCoverageIfUploaded: () => void;
}

/**
 * Every server write the statement step makes, and the ordering they need between them.
 *
 * Extracted from the section because the section is otherwise a render, and this is the part
 * with the failure modes: the step blob is replaced wholesale server-side with no merge, so
 * two writes that overlap silently drop each other's work. The queue and the two staleness
 * refs below are what stop that, and having them here means the concurrency can be tested
 * without mounting a component.
 *
 * Failures are reported to the member and swallowed, not thrown. Every one of them leaves a
 * recoverable state, and the alternative — an error boundary over the whole step — would take
 * out the card list a member could otherwise retry from.
 */
export default function useStatementStepWrites(): StatementStepWrites {
    const queryClient = useQueryClient();
    const { enqueueSnackbar } = useSnackbar();
    const { data: progress } = useAccountingOnboardingProgress();

    const updateStep = useUpdateOnboardingStep();
    // The three blob-free writes. This hook adds the step-blob bookkeeping each one needs.
    const {
        savePeriods: savePeriodsRequest,
        removeFromAccount,
        holdsMonthAfterRemoval,
        fileDocuments,
    } = useStatementDocumentWrites();

    /**
     * Set when this session uploaded something, so closing the modal only pays for a coverage
     * refetch when the held months could actually have changed.
     *
     * Deletes don't use this — they invalidate immediately, because the row vanishes from the
     * card behind the modal whether or not the member closes it.
     */
    const hasPendingCoverageChange = useRef(false);
    /**
     * The upload list as of the last write we sent, which runs ahead of the cached blob until
     * the progress refetch lands. Null whenever the server is authoritative.
     *
     * Every writer here replaces `uploaded_statements` wholesale, and `updateStep` only
     * invalidates progress in `onSuccess` — so a second write built from the cache inside that
     * window silently drops the first. Staging the month corrections removed the common case
     * (two corrections in a row), but not the cross-kind one: upload a file, then hit Save
     * before the refetch lands, and the new file's entry would go with it.
     */
    const sentUploadsRef = useRef<UploadedStatement[] | null>(null);
    /**
     * The same guarantee for the waiver set, which races the uploads in the other direction.
     *
     * Null whenever the server is authoritative. A queued upload write reads this rather than
     * the render it was scheduled from, so a waiver saved while that upload was still in the
     * queue is carried through instead of being replaced by the pre-waiver set.
     */
    const sentAcknowledgedRef = useRef<Set<string> | null>(null);
    /**
     * Serializes the step writes, because each replaces `uploaded_statements` wholesale and the
     * server cannot merge them.
     *
     * The upload path no longer races itself — the dropzone reports a settled batch once, so
     * one drop is one write — but delete, waiver and correction writes still overlap each
     * other, and any two of them read-then-replace the same list.
     *
     * Chained rather than debounced so every write survives, and `.then(task, task)` hands the
     * queue on through a failure as well as a success.
     */
    const writeQueueRef = useRef<Promise<unknown>>(Promise.resolve());

    const uploadedStatements = useMemo(
        () => progress?.statement_upload_data?.uploaded_statements ?? [],
        [progress?.statement_upload_data?.uploaded_statements]
    );

    const acknowledgedAccounts = useMemo(
        () => new Set(progress?.statement_upload_data?.acknowledged_accounts ?? []),
        [progress?.statement_upload_data?.acknowledged_accounts]
    );

    /**
     * The freshest server blob, readable from inside a queued write.
     *
     * A queued task closes over the render that scheduled it, so by the time it runs its
     * `uploadedStatements` can be several refetches stale. `sentUploadsRef` covers that while
     * it is set, but the effect below deliberately clears it whenever the server speaks — and
     * that is exactly when the closure is most out of date.
     */
    const latestUploadsRef = useRef<UploadedStatement[]>(uploadedStatements);
    /** The waiver set's equivalent, for the same reason. */
    const latestAcknowledgedRef = useRef<Set<string>>(acknowledgedAccounts);

    /**
     * How many writes are outstanding, so arriving news can be dated against them.
     *
     * The backstop behind `cancelQueries`. That cancels the refetches in flight when a write
     * *starts*; this covers one that starts while the write is already running — window focus
     * mid-PATCH. Either way the blob predates our write, and the effects below must not treat it
     * as authoritative: unguarded, waive A, the stale blob arrives reading `[]`, the staged ref is
     * nulled, and the next waiver sends `[B]` with A silently dropped.
     *
     * One count for the whole blob, not one per field, because every write sends both fields —
     * see the `data` payload below, where the field this caller is *not* changing is still read
     * out of its ref and sent. So an upload-only write is carrying the waiver set too, and a
     * refetch landing mid-upload can clear a staged waiver the upload is about to send.
     *
     * A count rather than a flag because writes queue: the second can be scheduled before the
     * first has settled, and a flag would be cleared by whichever finished first.
     *
     * Not airtight on its own — it is read when the effect flushes, and React Query notifies
     * through `setTimeout(cb, 0)`, so a refetch resolving in the same macrotask batch as our
     * settle can still find the count back at zero. `cancelQueries` is what closes the common
     * case; this narrows what is left.
     */
    const writesInFlightRef = useRef(0);

    // Once a fresh blob arrives it supersedes whatever we were holding locally — including
    // after a failed write, where the server's version is the truthful one. Unless one of our
    // own writes is still outstanding, in which case this blob predates it and is the older news
    // of the two; ours stands until it settles, and the invalidation on success brings a blob
    // that actually contains it.
    useEffect(() => {
        latestUploadsRef.current = uploadedStatements;
        if (writesInFlightRef.current > 0) {
            return;
        }
        sentUploadsRef.current = null;
    }, [uploadedStatements]);

    // Tracked separately from the uploads above: both fields come off the same blob, but each
    // ref is cleared by its own field so a write in flight is only ever superseded by news
    // about the thing it was writing.
    useEffect(() => {
        latestAcknowledgedRef.current = acknowledgedAccounts;
        if (writesInFlightRef.current > 0) {
            return;
        }
        sentAcknowledgedRef.current = null;
    }, [acknowledgedAccounts]);

    /** Run `task` after every write already queued, whether those succeeded or not. */
    const enqueueWrite = useCallback(<T>(task: () => Promise<T>): Promise<T> => {
        const next = writeQueueRef.current.then(task, task);
        writeQueueRef.current = next;
        return next;
    }, []);

    const persistStepData = useCallback(
        async (
            changes: { uploads?: UploadedStatement[]; acknowledged?: Set<string> },
            failureMessage: string
        ): Promise<boolean> => {
            // Before anything is staged: a progress GET issued *before* this write returns the
            // pre-write blob, and nothing in the payload dates it — `dataUpdatedAt` records when
            // a response arrived, not when its request left, so an already-in-flight refetch
            // always looks newer than us. Rather than try to date it, cancel it; React Query
            // discards a cancelled fetch's result instead of writing it to the cache.
            //
            // Awaiting also drains any notification already queued for a fetch that resolved a
            // moment ago, so the staging below cannot be cleared by news that predates it.
            await queryClient.cancelQueries({ queryKey: PROGRESS_QUERY_KEY });

            // Held so a failure can put them back. Staged optimistically below, and the rollback
            // has to be synchronous: the refetch the failure path kicks only clears the staging
            // once it lands, and the next write can be queued before that.
            const previousUploads = sentUploadsRef.current;
            const previousAcknowledged = sentAcknowledgedRef.current;
            if (changes.uploads) {
                // Recorded by the sender, not by each caller: the invariant is "whatever we
                // last sent is newer than the cache", and leaving it to three call sites to
                // remember meant a fourth writer would silently reintroduce the overwrite.
                sentUploadsRef.current = changes.uploads;
            }
            if (changes.acknowledged) {
                sentAcknowledgedRef.current = changes.acknowledged;
            }
            // Raised alongside the staging, dropped in `finally`: between these two points any
            // progress blob that arrives is older news than what we are sending. Unconditional,
            // because the payload below sends both fields whatever this caller changed.
            writesInFlightRef.current += 1;
            try {
                await updateStep.mutateAsync({
                    step: ONBOARDING_STEP,
                    status: 'in_progress',
                    data: {
                        // Refs, not the closure, for both fields — each write replaces the whole
                        // blob, so whichever field this caller is not changing still has to be
                        // sent, and sent current. An acknowledgement written while an upload is
                        // still in flight would otherwise send the pre-upload list and silently
                        // delete the row the member just watched appear; an upload queued behind
                        // a waiver would send the pre-waiver set and silently un-waive it.
                        uploaded_statements: changes.uploads ?? sentUploadsRef.current ?? latestUploadsRef.current,
                        acknowledged_accounts: [
                            ...(changes.acknowledged ?? sentAcknowledgedRef.current ?? latestAcknowledgedRef.current),
                        ],
                    },
                });
            } catch (error) {
                // Unstage what did not land, so the next write is built from the server's
                // list rather than this one's. Left forward, a later delete or waiver reads
                // entries the server never accepted and persists them wholesale — quietly
                // resurrecting a row for a file this failure left unfiled.
                //
                // Guarded on identity, not merely on the field being present: a progress
                // refetch can land mid-flight and clear the ref, and that news outranks
                // ours. If the ref no longer holds what we staged, someone else has spoken.
                if (changes.uploads && sentUploadsRef.current === changes.uploads) {
                    sentUploadsRef.current = previousUploads;
                }
                if (changes.acknowledged && sentAcknowledgedRef.current === changes.acknowledged) {
                    sentAcknowledgedRef.current = previousAcknowledged;
                }
                // The cancel at the top of this write also killed whatever refetch the *previous*
                // write's `onSuccess` invalidation had in flight, and `updateStep` invalidates on
                // success only — so a failure here would otherwise leave the cache showing data
                // from before that earlier write until the next poll or refocus.
                queryClient.invalidateQueries({ queryKey: PROGRESS_QUERY_KEY });
                sendDatadogRumError('Statement upload step data write failed', error, {
                    source: 'useStatementStepWrites.persistStepData',
                });
                enqueueSnackbar(failureMessage, { variant: 'error', autoHideDuration: ERROR_SNACKBAR_DURATION_MS });
                return false;
            } finally {
                writesInFlightRef.current -= 1;
            }
            return true;
        },
        [updateStep, enqueueSnackbar, queryClient]
    );

    const saveUploads = useCallback(
        async (uploaded: Document[], account: AccountStatementCoverage) => {
            hasPendingCoverageChange.current = true;
            // Kicked before the step write, not after: the document list backs the rows
            // themselves and nothing in it depends on the step blob, so chaining the two would
            // just delay the row by a round trip. Coverage is left alone — it only changes
            // once a job lands.
            queryClient.invalidateQueries({ queryKey: ['documents'] });

            // Read and write inside one queued step. Computing the payload out here would
            // reintroduce the race it exists to close: two concurrent handlers would both read
            // the same list before either had written.
            const addedUuids = await enqueueWrite(async () => {
                // The freshest list we know of: ours if a write is still in flight, else the
                // server's.
                const current = sentUploadsRef.current ?? latestUploadsRef.current;
                const known = new Set(current.map((entry) => entry.member_document_id));
                // The dropzone reports its whole accumulated session, not just the newest
                // file, so anything already recorded has to be filtered out or a second
                // upload re-appends the first.
                const additions = uploaded
                    .filter((document) => !known.has(document.uuid))
                    .map((document) => ({
                        member_document_id: document.uuid,
                        file_name: document.file_name,
                        account_key: accountKey(account),
                        uploaded_at: new Date().toISOString(),
                    }));

                if (additions.length === 0) {
                    return [];
                }

                // Filing is gated on this landing, hence the return value. A document filed
                // against the account but missing from the blob is credited by coverage and
                // listed by nothing — the modal builds its rows from this list — which is
                // the file-the-card-credits-but-the-modal-never-shows state `removeUpload`
                // keeps its entry to avoid, reached from the other side. Unfiled, the
                // failure is inert and says what the member is already being told.
                if (!(await persistStepData({ uploads: [...current, ...additions] }, UPLOAD_SAVE_FAILED_MESSAGE))) {
                    return [];
                }
                return additions.map((entry) => entry.member_document_id);
            });

            // Outside the write queue, and keyed off what that queue decided was new. The
            // queue orders writes to the step blob, which this does not touch; joining it
            // would make every later upload's row wait behind these round trips. Reusing its
            // dedupe still matters, though — the dropzone re-reports files it has already
            // announced, and each redundant assignment is another live Tight call.
            // Silent on failure by design: the file still uploads and still has its account
            // resolved asynchronously, and the modal reads the statuses endpoint to find out.
            const { recorded } = await fileDocuments(addedUuids, account.bank_account_id);
            const recordedAny = recorded.length > 0;
            if (recordedAny) {
                // The assignments live on the progress blob, and the step write above
                // invalidated it *before* these calls ran — so without this the cached copy
                // never learns about them.
                queryClient.invalidateQueries({ queryKey: PROGRESS_QUERY_KEY });
                // The verdicts themselves moved to their own endpoint, so refreshing the blob no
                // longer refreshes them. Without this the files just uploaded stay pinned to the
                // cautious "still landing" reading until the next poll tick.
                queryClient.invalidateQueries({ queryKey: STATEMENT_UPLOAD_STATUSES_QUERY_KEY });
            }
        },
        [persistStepData, enqueueWrite, queryClient, fileDocuments]
    );

    const removeUpload = useCallback(
        async (documentUuid: string, account: AccountStatementCoverage, period?: StatementPeriod) => {
            // Keep the entry on failure: the file survived and its tracker row still lands, so
            // dropping it would leave a file the card credits but the modal never shows.
            if (!(await removeFromAccount(documentUuid, account, period))) {
                return;
            }
            // Kept, too, while the file still holds another month on this card.
            if (holdsMonthAfterRemoval(documentUuid, account, period)) {
                return;
            }
            const key = accountKey(account);
            await enqueueWrite(async () => {
                // Only this card's entry; the same file's entry under another card is that card's.
                const current = sentUploadsRef.current ?? latestUploadsRef.current;
                const uploads = current.filter(
                    (entry) => entry.member_document_id !== documentUuid || entry.account_key !== key
                );
                // Another removal of the same file may have dropped it already.
                if (uploads.length !== current.length) {
                    await persistStepData({ uploads }, SAVE_FAILED_MESSAGE);
                }
            });
        },
        [removeFromAccount, holdsMonthAfterRemoval, persistStepData, enqueueWrite]
    );

    /**
     * Passed straight through. The step blob holds no month assignments — the server owns them,
     * and `held_periods` is that record read back — so there is nothing for this hook to add.
     */
    const savePeriods = savePeriodsRequest;

    const saveAcknowledged = useCallback(
        (update: (current: Set<string>) => Set<string>) =>
            enqueueWrite(() => {
                // Read inside the queued write, never in the caller's render: ours if a write is
                // still in flight, else the server's. See the interface for what reading it
                // outside costs.
                const current = sentAcknowledgedRef.current ?? latestAcknowledgedRef.current;
                return persistStepData({ acknowledged: update(current) }, SAVE_FAILED_MESSAGE);
            }),
        [enqueueWrite, persistStepData]
    );

    const refreshCoverageIfUploaded = useCallback(() => {
        if (!hasPendingCoverageChange.current) {
            return;
        }
        hasPendingCoverageChange.current = false;
        queryClient.invalidateQueries({ queryKey: STATEMENT_COVERAGE_QUERY_KEY });
    }, [queryClient]);

    return {
        uploadedStatements,
        acknowledgedAccounts,
        saveUploads,
        removeUpload,
        savePeriods,
        saveAcknowledged,
        refreshCoverageIfUploaded,
    };
}
